// src/features/secret/DatabasePanel.tsx
// Database engine workspace — dynamic role CRUD with SQL statement editors, and one-time
// credential generation with lease display. See docs/ux/secret-ia.md section 1c.

import { useEffect, useState, useCallback } from 'react';
import { useI18n, useSecretDatabaseSecret } from '@react/hooks';
import { Button, Input, Textarea, Alert, ErrorAlert } from '@react/components';
import { Select } from '@/components/Select';
import { TypedConfirmDialog } from './TypedConfirmDialog';
import { FormDialog } from '@/components/form';
import type { DatabaseRole, DatabaseBackendType, DynamicCredentials } from '@core/types/secret';
import { DatabaseBackendTypeValues } from '@core/types/secret';
import { formatTtlSeconds, enumSuffix } from './logic';

// Statement editors show SQL templates ({{name}}/{{password}}) — always render in mono
// (docs/ux/14-forms.md). The generated Textarea pins its own font-family in CSS, so an inline
// style (forwarded through the props spread) is what actually wins over it.
const MONO_STYLE = { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace' } as const;

function parsePositiveIntHours(value: string): number | null {
  const trimmed = value.trim();
  if (!/^\d+$/.test(trimmed)) return null;
  const n = parseInt(trimmed, 10);
  return n > 0 ? n : null;
}

function useCopyToClipboard() {
  const [copied, setCopied] = useState(false);
  const copy = useCallback(async (text: string) => {
    try { await navigator.clipboard.writeText(text); } catch { /* clipboard unavailable */ }
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }, []);
  return { copied, copy };
}

const BACKEND_TYPES = DatabaseBackendTypeValues.filter((v) => v !== 'DATABASE_BACKEND_TYPE_UNSPECIFIED');

function backendLabelKey(type?: DatabaseBackendType): string {
  return `secret.db.backend.${enumSuffix(type, 'DATABASE_BACKEND_TYPE_')}`;
}

function CreateRoleForm({ open, engineId, onCreated, onClose }: { open: boolean; engineId: string; onCreated: () => void; onClose: () => void }) {
  const { t } = useI18n();
  const store = useSecretDatabaseSecret();
  const [name, setName] = useState('');
  const [dbType, setDbType] = useState<DatabaseBackendType>('DATABASE_BACKEND_TYPE_POSTGRESQL');
  const [connectionUrl, setConnectionUrl] = useState('');
  const [creationStatements, setCreationStatements] = useState('');
  const [revocationStatements, setRevocationStatements] = useState('');
  const [defaultTtlHours, setDefaultTtlHours] = useState('1');
  const [maxTtlHours, setMaxTtlHours] = useState('24');
  const [errors, setErrors] = useState<{ creationStatements?: string; defaultTtlHours?: string; maxTtlHours?: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<unknown>(null);

  const dirty = !!(name.trim() || connectionUrl.trim() || creationStatements.trim() || revocationStatements.trim());

  const reset = () => {
    setName('');
    setDbType('DATABASE_BACKEND_TYPE_POSTGRESQL');
    setConnectionUrl('');
    setCreationStatements('');
    setRevocationStatements('');
    setDefaultTtlHours('1');
    setMaxTtlHours('24');
    setErrors({});
    setSubmitError(null);
  };

  const close = () => { reset(); onClose(); };

  const submit = async () => {
    if (!name.trim() || !connectionUrl.trim()) return;
    const fieldErrors: typeof errors = {};
    if (!creationStatements.trim()) fieldErrors.creationStatements = t('secret.validation.creationStatementsRequired');
    const defaultTtl = parsePositiveIntHours(defaultTtlHours);
    if (defaultTtl == null) fieldErrors.defaultTtlHours = t('secret.validation.positiveIntegerRequired');
    const maxTtl = parsePositiveIntHours(maxTtlHours);
    if (maxTtl == null) fieldErrors.maxTtlHours = t('secret.validation.positiveIntegerRequired');
    if (Object.keys(fieldErrors).length > 0) {
      setErrors(fieldErrors);
      return;
    }
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      await store.CreateDatabaseRole(engineId, {
        engine_id: engineId,
        role: {
          name: name.trim(),
          db_type: dbType,
          connection_url: connectionUrl.trim(),
          creation_statements: creationStatements.split('\n').map((s) => s.trim()).filter(Boolean),
          revocation_statements: revocationStatements.split('\n').map((s) => s.trim()).filter(Boolean),
          default_ttl_seconds: defaultTtl! * 3600,
          max_ttl_seconds: maxTtl! * 3600,
        } as DatabaseRole,
      });
      reset();
      onCreated();
    } catch (err) {
      setSubmitError(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <FormDialog
      open={open}
      onClose={close}
      onSubmit={submit}
      submitLabel={t('common.create')}
      canSubmit={!!name.trim() && !!connectionUrl.trim()}
      isSubmitting={isSubmitting}
      dirty={dirty}
      width="lg"
      aria-label={t('secret.db.createRole')}
    >
      <div className="space-y-3 px-3.5 pb-4 pt-2">
        {submitError != null && <ErrorAlert error={submitError} dismissible onDismiss={() => setSubmitError(null)} />}
        <Input label={t('secret.db.field.roleName')} value={name} onChange={(e) => setName(e.target.value)} data-autofocus />
        <Select
          label={t('secret.db.field.backend')}
          value={dbType}
          onChange={(e) => setDbType(e.target.value as DatabaseBackendType)}
          options={BACKEND_TYPES.map((v) => ({ value: v, label: t(backendLabelKey(v)) }))}
        />
        <Input label={t('secret.db.field.connectionUrl')} value={connectionUrl} onChange={(e) => setConnectionUrl(e.target.value)} placeholder="postgresql://user:pass@host:5432/db" style={MONO_STYLE} />
        <Textarea
          label={t('secret.db.field.creationStatements')}
          value={creationStatements}
          onChange={(e) => { setCreationStatements(e.target.value); setErrors((prev) => ({ ...prev, creationStatements: undefined })); }}
          rows={4}
          helperText={t('secret.db.field.creationStatementsHelp')}
          error={errors.creationStatements}
          placeholder={"CREATE ROLE \"{{name}}\" WITH LOGIN PASSWORD '{{password}}' VALID UNTIL '{{expiration}}';"}
          style={MONO_STYLE}
        />
        <Textarea
          label={t('secret.db.field.revocationStatements')}
          value={revocationStatements}
          onChange={(e) => setRevocationStatements(e.target.value)}
          rows={3}
          placeholder={"DROP ROLE IF EXISTS \"{{name}}\";"}
          style={MONO_STYLE}
        />
        <div className="grid grid-cols-2 gap-4">
          <Input
            label={t('secret.db.field.defaultTtlHours')}
            value={defaultTtlHours}
            onChange={(e) => { setDefaultTtlHours(e.target.value); setErrors((prev) => ({ ...prev, defaultTtlHours: undefined })); }}
            inputMode="numeric"
            error={errors.defaultTtlHours}
          />
          <Input
            label={t('secret.db.field.maxTtlHours')}
            value={maxTtlHours}
            onChange={(e) => { setMaxTtlHours(e.target.value); setErrors((prev) => ({ ...prev, maxTtlHours: undefined })); }}
            inputMode="numeric"
            error={errors.maxTtlHours}
          />
        </div>
      </div>
    </FormDialog>
  );
}

function GeneratedCredentialsBanner({ creds, onDismiss }: { creds: DynamicCredentials; onDismiss: () => void }) {
  const { t } = useI18n();
  const { copied, copy } = useCopyToClipboard();
  const blob = `username: ${creds.username}\npassword: ${creds.password}\nlease_id: ${creds.lease_id}`;
  return (
    <Alert variant="success" dismissible onDismiss={onDismiss}>
      <div className="space-y-2">
        <p className="text-sm font-medium">{t('secret.db.credentialsGenerated')}</p>
        <p className="text-xs text-muted-foreground">{t('secret.db.credentialsWarning')}</p>
        <div className="text-sm font-mono space-y-1">
          <div>{t('secret.db.username')}: <span className="select-all">{creds.username}</span></div>
          <div>{t('secret.db.password')}: <span className="select-all">{creds.password}</span></div>
          <div className="text-xs text-muted-foreground">{t('secret.db.lease', { id: creds.lease_id, ttl: formatTtlSeconds(creds.lease_duration_seconds) })}</div>
        </div>
        <Button variant="secondary" size="sm" onClick={() => copy(blob)}>{copied ? t('common.copied') : t('secret.db.copyCredentials')}</Button>
      </div>
    </Alert>
  );
}

function RoleRow({ engineId, role, onDeleted }: { engineId: string; role: DatabaseRole; onDeleted: () => void }) {
  const { t } = useI18n();
  const store = useSecretDatabaseSecret();
  const [expanded, setExpanded] = useState(false);
  const [detail, setDetail] = useState<DatabaseRole | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [isActing, setIsActing] = useState(false);
  const [creds, setCreds] = useState<DynamicCredentials | null>(null);

  const toggleExpand = () => {
    const next = !expanded;
    setExpanded(next);
    if (next && !detail) {
      // GetDatabaseRole — the authoritative single-role read, including full statement text
      // (ListDatabaseRoles already returns the statements, but this keeps the detail view live).
      store.GetDatabaseRole(engineId, role.name).then((r) => setDetail(r.role ?? null)).catch(() => {});
    }
  };

  const doGenerate = async () => {
    setIsActing(true);
    try {
      const resp = await store.GenerateCredentials(engineId, role.name, { engine_id: engineId, role_name: role.name });
      setCreds(resp.credentials ?? null);
    } finally {
      setIsActing(false);
    }
  };

  const doDelete = async () => {
    setIsActing(true);
    try { await store.DeleteDatabaseRole(engineId, role.name); setDeleteOpen(false); onDeleted(); } finally { setIsActing(false); }
  };

  return (
    <li className="p-3 space-y-2">
      <div className="flex items-center justify-between">
        <button onClick={toggleExpand} className="text-left">
          <p className="text-sm font-medium text-foreground">{role.name}</p>
          <p className="text-xs text-muted-foreground">{t(backendLabelKey(role.db_type))} · {t('secret.db.maxTtl', { ttl: formatTtlSeconds(role.max_ttl_seconds) })}</p>
        </button>
        <div className="flex items-center gap-2">
          <Button size="sm" onClick={doGenerate} disabled={isActing}>{isActing ? t('common.loading') : t('secret.db.generateCredentials')}</Button>
          <Button variant="ghost" size="sm" className="text-destructive" onClick={() => setDeleteOpen(true)}>{t('common.delete')}</Button>
        </div>
      </div>
      {creds && <GeneratedCredentialsBanner creds={creds} onDismiss={() => setCreds(null)} />}
      {expanded && (
        <div className="rounded-md bg-muted/40 p-2 text-xs font-mono space-y-1">
          {detail ? detail.creation_statements.map((s, i) => <div key={i}>{s}</div>) : t('common.loading')}
        </div>
      )}
      <TypedConfirmDialog
        open={deleteOpen}
        title={t('secret.db.deleteRoleTitle')}
        description={t('secret.db.deleteRoleDescription')}
        phrase={role.name}
        confirmLabel={t('common.delete')}
        isConfirming={isActing}
        onConfirm={doDelete}
        onCancel={() => setDeleteOpen(false)}
      />
    </li>
  );
}

export function DatabasePanel({ engineId }: { engineId: string }) {
  const { t } = useI18n();
  const store = useSecretDatabaseSecret();
  const [roles, setRoles] = useState<DatabaseRole[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    store.ListDatabaseRoles(engineId).then((r) => setRoles(r.roles ?? [])).catch(() => setRoles([])).finally(() => setLoading(false));
  }, [engineId, store]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="flex-1 overflow-y-auto p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-foreground">{t('secret.db.rolesTitle')}</h2>
        <Button size="sm" onClick={() => setShowCreate(true)}>{t('secret.db.createRole')}</Button>
      </div>

      <CreateRoleForm open={showCreate} engineId={engineId} onCreated={() => { setShowCreate(false); load(); }} onClose={() => setShowCreate(false)} />

      {loading ? (
        <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
      ) : roles.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('secret.db.noRoles')}</p>
      ) : (
        <ul className="divide-y divide-border rounded-lg border border-border">
          {roles.map((role) => <RoleRow key={role.name} engineId={engineId} role={role} onDeleted={load} />)}
        </ul>
      )}
    </div>
  );
}
